import { useMemo, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  Timestamp,
  orderBy,
  limit,
  type QueryConstraint,
} from 'firebase/firestore';
import { httpsCallable } from '../lib/firebase/authReadyCallable';
import { db, functions } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { dailyActionsKeys } from './crm/useDailyActions';
import { toMillisSafe } from '../lib/utils/timestamp';
import type {
  Lead,
  LeadActivity,
  LeadFilters,
  LeadStatus,
  LeadOutcome,
  PipelineStage,
  PipelineMetrics,
  StageMetrics,
  OwnerMetrics,
  BranchMetrics,
  CustomFieldValue,
} from '../types/lead';
import { withNeedsSignal } from '../lib/needsSignalMutation';

// Query key factory
export const leadKeys = {
  all: ['leads'] as const,
  lists: () => [...leadKeys.all, 'list'] as const,
  list: (filters: LeadFilters) => [...leadKeys.lists(), filters] as const,
  byPipeline: (pipelineId: string) => [...leadKeys.lists(), 'pipeline', pipelineId] as const,
  byStage: (pipelineId: string, status?: LeadStatus) => [...leadKeys.lists(), 'byStage', pipelineId, status] as const,
  details: () => [...leadKeys.all, 'detail'] as const,
  detail: (id: string) => [...leadKeys.details(), id] as const,
  activities: (leadId: string) => [...leadKeys.all, 'activities', leadId] as const,
  metrics: (pipelineId: string) => [...leadKeys.all, 'metrics', pipelineId] as const,
  stats: () => [...leadKeys.all, 'stats'] as const,
};

// ============================================
// LEAD QUERIES
// ============================================

/**
 * Fetch all leads for the current organization with optional filters.
 *
 * @example
 * ```tsx
 * // Basic usage - fetch all leads
 * const { data: leads, isLoading, error } = useLeads();
 *
 * // With filters
 * const { data: leads } = useLeads({
 *   pipelineId: 'sales-pipeline',
 *   status: 'open',
 *   ownerId: userId,
 *   searchTerm: 'acme',
 * });
 * ```
 *
 * @param filters - Optional filter criteria
 * @param filters.pipelineId - Filter by pipeline ID
 * @param filters.stageId - Filter by single stage ID
 * @param filters.stageIds - Filter by multiple stage IDs
 * @param filters.ownerId - Filter by owner user ID
 * @param filters.branch - Filter by branch name
 * @param filters.status - Filter by status ('open' | 'closed')
 * @param filters.outcome - Filter by outcome ('won' | 'lost' | 'no_decision')
 * @param filters.serviceTags - Filter by service tags
 * @param filters.priority - Filter by priority level
 * @param filters.source - Filter by lead source
 * @param filters.minValue - Minimum estimated value
 * @param filters.maxValue - Maximum estimated value
 * @param filters.searchTerm - Search company name, contact, email, phone, context
 *
 * @returns React Query result with Lead[] data
 */
/**
 * Number of leads to fetch per stage during the initial distributed load.
 * Ensures every funnel phase has visible data immediately.
 */
const PER_STAGE_INITIAL_LIMIT = 10;

/**
 * Maximum total leads for the paginated "full" load. Users click "Load More" beyond this.
 */
const PAGINATED_LOAD_LIMIT = 100;

/**
 * Build Firestore constraints and fetch leads, applying client-side filters and sorting.
 * Shared by both the quick (limited) and full queries.
 */
async function fetchLeadsFromFirestore(
  organizationId: string,
  stableFilters: Omit<LeadFilters, 'searchTerm'>,
  fetchLimit?: number
): Promise<Lead[]> {
  // Build Firestore constraints dynamically for high-cardinality filters
  // This reduces data transfer by filtering at the database level
  const constraints: QueryConstraint[] = [
    where('organizationId', '==', organizationId),
  ];

  // Add Firestore-level filters (indexed, high impact on data reduction)
  if (stableFilters.pipelineId) {
    constraints.push(where('pipelineId', '==', stableFilters.pipelineId));
  }
  if (stableFilters.status) {
    constraints.push(where('status', '==', stableFilters.status));
  }
  if (stableFilters.ownerId) {
    constraints.push(where('ownerId', '==', stableFilters.ownerId));
  }
  // Equality filters — safe to combine freely with the above and with each
  // other. Pushed server-side so the `limit(fetchLimit)` "quick" phase below
  // truncates an ALREADY-filtered set instead of truncating before these
  // filters run (the PR #2277/#2288 defect class).
  if (stableFilters.stageId) {
    constraints.push(where('stageId', '==', stableFilters.stageId));
  }
  if (stableFilters.branch) {
    constraints.push(where('branch', '==', stableFilters.branch));
  }
  if (stableFilters.outcome) {
    constraints.push(where('outcome', '==', stableFilters.outcome));
  }
  if (stableFilters.priority) {
    constraints.push(where('priority', '==', stableFilters.priority));
  }
  if (stableFilters.source) {
    constraints.push(where('source', '==', stableFilters.source));
  }
  // Firestore allows only ONE `in`/`array-contains-any` (disjunctive) clause
  // per query. stageId (single-value equality, above) takes priority over
  // stageIds; among stageIds/serviceTags/tags, push the first one present —
  // any remaining disjunctive filters fall back to a client-side filter
  // below (still correct, just less selective at the DB layer).
  let disjunctivePushed = !!stableFilters.stageId;
  if (!disjunctivePushed && stableFilters.stageIds && stableFilters.stageIds.length > 0) {
    constraints.push(where('stageId', 'in', stableFilters.stageIds.slice(0, 30)));
    disjunctivePushed = true;
  }
  const serviceTagsPushedServerSide =
    !disjunctivePushed && !!stableFilters.serviceTags && stableFilters.serviceTags.length > 0;
  if (serviceTagsPushedServerSide) {
    constraints.push(where('serviceTags', 'array-contains-any', stableFilters.serviceTags!.slice(0, 30)));
    disjunctivePushed = true;
  }
  const tagsPushedServerSide =
    !disjunctivePushed && !!stableFilters.tags && stableFilters.tags.length > 0;
  if (tagsPushedServerSide) {
    constraints.push(where('tags', 'array-contains-any', stableFilters.tags!.slice(0, 30)));
  }

  // Always sort by updatedAt descending
  constraints.push(orderBy('updatedAt', 'desc'));

  // minValue/maxValue can't be pushed server-side without an orderBy on
  // estimatedValue (Firestore requires the first orderBy to match an
  // inequality field), which would conflict with the updatedAt sort above.
  // Rather than truncate-then-filter, skip the capped "quick" phase entirely
  // when either is present so filtering runs over the complete matching set
  // (mirrors the existing loadAll/fullQuery path, which already fetches
  // everything before filtering).
  const hasValueRangeFilter =
    stableFilters.minValue !== undefined || stableFilters.maxValue !== undefined;
  const effectiveFetchLimit = hasValueRangeFilter ? undefined : fetchLimit;

  if (effectiveFetchLimit) {
    constraints.push(limit(effectiveFetchLimit));
  }

  const q = query(collection(db, COLLECTIONS.LEADS), ...constraints);

  const snapshot = await getDocs(q);
  let leads = snapshot.docs.map((d) => ({
    ...d.data(),
    leadId: d.id,
  })) as Lead[];

  // Filter archived leads client-side (avoids Firestore != operator which
  // forces implicit orderBy and requires composite indexes)
  leads = leads.filter((l) => l.isArchived !== true);

  // Client-side fallback for any disjunctive filter that couldn't be pushed
  // server-side because Firestore already used its one allowed
  // in/array-contains-any slot for another filter (see disjunctivePushed
  // above). These run over the already-narrowed server-filtered set.
  if (!disjunctivePushed && stableFilters.stageIds && stableFilters.stageIds.length > 0) {
    leads = leads.filter((l) => stableFilters.stageIds!.includes(l.stageId));
  }
  if (!serviceTagsPushedServerSide && stableFilters.serviceTags && stableFilters.serviceTags.length > 0) {
    leads = leads.filter((l) =>
      l.serviceTags?.some((tag) => stableFilters.serviceTags!.includes(tag))
    );
  }
  if (!tagsPushedServerSide && stableFilters.tags && stableFilters.tags.length > 0) {
    leads = leads.filter((l) =>
      l.tags?.some((tag) => stableFilters.tags!.includes(tag))
    );
  }
  if (stableFilters.minValue !== undefined) {
    leads = leads.filter((l) => (l.estimatedValue || 0) >= stableFilters.minValue!);
  }
  if (stableFilters.maxValue !== undefined) {
    leads = leads.filter((l) => (l.estimatedValue || 0) <= stableFilters.maxValue!);
  }

  // Apply client-side sorting
  if (stableFilters.sortBy) {
    const { field, direction } = stableFilters.sortBy;
    const multiplier = direction === 'desc' ? -1 : 1;

    leads.sort((a, b) => {
      let aVal: number | string | undefined;
      let bVal: number | string | undefined;

      switch (field) {
        case 'score':
          aVal = a.score?.total ?? 0;
          bVal = b.score?.total ?? 0;
          break;
        case 'estimatedValue':
          aVal = a.estimatedValue ?? 0;
          bVal = b.estimatedValue ?? 0;
          break;
        case 'lastContactedAt':
          aVal = toMillisSafe(a.lastContactedAt) ?? 0;
          bVal = toMillisSafe(b.lastContactedAt) ?? 0;
          break;
        case 'createdAt':
          aVal = a.createdAt?.toMillis() ?? 0;
          bVal = b.createdAt?.toMillis() ?? 0;
          break;
        case 'updatedAt':
          aVal = a.updatedAt?.toMillis() ?? 0;
          bVal = b.updatedAt?.toMillis() ?? 0;
          break;
        case 'companyName':
          aVal = a.companyName.toLowerCase();
          bVal = b.companyName.toLowerCase();
          break;
        default:
          return 0;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * multiplier;
      }
      return ((aVal as number) - (bVal as number)) * multiplier;
    });
  }

  return leads;
}

export function useLeads(filters: LeadFilters = {}) {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;

  // Exclude searchTerm from query key — search is purely client-side and including it
  // causes a new cache entry per keystroke, triggering refetches that lose input focus.
  const stableFilters = useMemo(
    () => {
      const { searchTerm, ...rest } = filters;
      return rest;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      filters.pipelineId, filters.stageId, filters.ownerId, filters.branch,
      filters.status, filters.outcome, filters.priority, filters.source,
      filters.minValue, filters.maxValue, filters.showStale,
      // Arrays — serialize for stable comparison
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters.stageIds),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters.stageGroups),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters.serviceTags),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters.tags),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters.dateRange),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(filters.sortBy),
    ]
  );

  // Phase 1: Fast initial load — first 100 leads sorted by updatedAt.
  // Larger than old limit (50) to ensure data across more funnel stages.
  const quickQuery = useQuery({
    queryKey: [...leadKeys.list(stableFilters), 'quick'],
    queryFn: async (): Promise<Lead[]> => {
      if (!organizationId) return [];
      return fetchLeadsFromFirestore(organizationId, stableFilters, PAGINATED_LOAD_LIMIT);
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 2,
  });

  // Phase 2: Full load — all remaining leads, opt-in via loadAll flag.
  // Prevents automatic download of 1000+ leads on page mount.
  const [loadAllRequested, setLoadAllRequested] = useState(false);

  const fullQuery = useQuery({
    queryKey: leadKeys.list(stableFilters),
    queryFn: async (): Promise<Lead[]> => {
      if (!organizationId) return [];
      return fetchLeadsFromFirestore(organizationId, stableFilters);
    },
    enabled: !!organizationId && quickQuery.isSuccess && loadAllRequested,
    staleTime: 1000 * 60 * 2,
    placeholderData: quickQuery.data,
  });

  // Use full data when available, quick data otherwise
  const resolvedData = fullQuery.data ?? quickQuery.data;

  // Detect if quick query returned exactly the limit (likely more data available)
  const hasMore = quickQuery.data?.length === PAGINATED_LOAD_LIMIT && !loadAllRequested;

  // Load all remaining leads — call this from a "Load More" button
  const loadAll = useCallback(() => setLoadAllRequested(true), []);

  // Search filtering applied post-query (no cache key impact)
  const data = useMemo(() => {
    if (!filters.searchTerm || !resolvedData) return resolvedData;
    const term = filters.searchTerm.toLowerCase();
    return resolvedData.filter(
      (l) =>
        l.companyName.toLowerCase().includes(term) ||
        l.primaryContactName?.toLowerCase().includes(term) ||
        l.context?.toLowerCase().includes(term) ||
        l.email?.toLowerCase().includes(term) ||
        l.phone?.includes(term) ||
        l.source?.toLowerCase().includes(term)
    );
  }, [resolvedData, filters.searchTerm]);

  // isLoading: true only while the first batch is loading (skeleton state)
  // isLoadingMore: true while the full set is loading after the initial batch is visible
  const isLoadingMore = quickQuery.isSuccess && fullQuery.isFetching && !fullQuery.isSuccess;

  const isError = fullQuery.isError || quickQuery.isError;
  const error = fullQuery.error ?? quickQuery.error;

  return {
    ...fullQuery,
    data,
    isLoading: quickQuery.isLoading,
    isLoadingMore,
    isError,
    error,
    /** True when initial batch hit the limit and more data may be available */
    hasMore,
    /** Call to trigger loading all remaining leads beyond the initial 100 */
    loadAll,
  };
}

/**
 * Smart per-stage lead loading for Kanban views.
 *
 * Phase 1: Fetches top N leads per stage in parallel (distributed across funnel).
 * Phase 2: "Load More" expands individual stages on demand.
 *
 * This replaces the old pattern of loading all leads then grouping client-side,
 * which was slow for pipelines with 500+ leads.
 */
export function useLeadsByStageDistributed(
  pipelineId: string,
  stages: PipelineStage[],
  options?: { statusFilter?: LeadStatus; ownerId?: string; perStageLimit?: number }
) {
  const { currentOrganization } = useOrganization();
  const organizationId = currentOrganization?.organizationId;
  const perStageLimit = options?.perStageLimit ?? PER_STAGE_INITIAL_LIMIT;

  return useQuery({
    queryKey: [...leadKeys.byStage(pipelineId, options?.statusFilter), 'distributed', perStageLimit],
    queryFn: async (): Promise<{
      byStage: Record<string, Lead[]>;
      truncatedStages: string[];
      totalLoaded: number;
    }> => {
      if (!organizationId || !pipelineId || stages.length === 0) {
        return { byStage: {}, truncatedStages: [], totalLoaded: 0 };
      }

      // Fire parallel per-stage queries — each stage gets its own LIMIT
      const stageQueries = stages.map(async (stage) => {
        const constraints: QueryConstraint[] = [
          where('organizationId', '==', organizationId),
          where('pipelineId', '==', pipelineId),
          where('stageId', '==', stage.stageId),
        ];
        if (options?.statusFilter) {
          constraints.push(where('status', '==', options.statusFilter));
        }
        if (options?.ownerId) {
          constraints.push(where('ownerId', '==', options.ownerId));
        }
        // Order by score DESC so highest-value leads appear first in each stage
        constraints.push(orderBy('updatedAt', 'desc'));
        // Fetch limit + 1 to detect if there are more
        constraints.push(limit(perStageLimit + 1));

        const q = query(collection(db, COLLECTIONS.LEADS), ...constraints);
        const snapshot = await getDocs(q);
        const leads = snapshot.docs
          .map((d) => ({ ...d.data(), leadId: d.id } as Lead))
          .filter((l) => l.isArchived !== true);

        const hasMore = leads.length > perStageLimit;
        return {
          stageId: stage.stageId,
          leads: hasMore ? leads.slice(0, perStageLimit) : leads,
          hasMore,
        };
      });

      const results = await Promise.all(stageQueries);

      const byStage: Record<string, Lead[]> = {};
      const truncatedStages: string[] = [];
      let totalLoaded = 0;

      for (const result of results) {
        byStage[result.stageId] = result.leads;
        totalLoaded += result.leads.length;
        if (result.hasMore) {
          truncatedStages.push(result.stageId);
        }
      }

      return { byStage, truncatedStages, totalLoaded };
    },
    enabled: !!organizationId && !!pipelineId && stages.length > 0,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch leads grouped by stage for Kanban board view.
 *
 * Returns a Record where keys are stage IDs and values are arrays of leads in that stage.
 * Useful for rendering a pipeline as columns with cards.
 *
 * @example
 * ```tsx
 * const { data: leadsByStage, isLoading } = useLeadsByStage(
 *   pipeline.pipelineId,
 *   pipeline.stages,
 *   'open' // Only show open leads
 * );
 *
 * // Render Kanban columns
 * {pipeline.stages.map(stage => (
 *   <Column key={stage.stageId}>
 *     {leadsByStage?.[stage.stageId]?.map(lead => (
 *       <LeadCard lead={lead} />
 *     ))}
 *   </Column>
 * ))}
 * ```
 *
 * @param pipelineId - Pipeline ID to fetch leads for
 * @param stages - Array of stage configurations (used to initialize empty arrays)
 * @param statusFilter - Optional status filter ('open' | 'closed')
 *
 * @returns React Query result with Record<string, Lead[]> data
 */
export function useLeadsByStage(pipelineId: string, stages: PipelineStage[], statusFilter?: LeadStatus) {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: leadKeys.byStage(pipelineId, statusFilter),
    queryFn: async (): Promise<Record<string, Lead[]>> => {
      if (!currentOrganization?.organizationId || !pipelineId) {
        return {};
      }

      // Build query constraints - status filter is optional
      const constraints: QueryConstraint[] = [
        where('organizationId', '==', currentOrganization.organizationId),
        where('pipelineId', '==', pipelineId),
      ];
      if (statusFilter) {
        constraints.push(where('status', '==', statusFilter));
      }
      constraints.push(orderBy('stageOrder', 'asc'), orderBy('updatedAt', 'desc'));

      const q = query(collection(db, COLLECTIONS.LEADS), ...constraints);

      const snapshot = await getDocs(q);
      const leads = snapshot.docs.map((doc) => ({
        ...doc.data(),
        leadId: doc.id,
      })) as Lead[];

      // Filter archived leads client-side (avoids Firestore != operator which
      // forces implicit orderBy and requires composite indexes)
      const activeLeads = leads.filter((l) => l.isArchived !== true);

      // Group by stage
      const grouped: Record<string, Lead[]> = {};
      stages.forEach((stage) => {
        grouped[stage.stageId] = [];
      });

      activeLeads.forEach((lead) => {
        if (grouped[lead.stageId]) {
          grouped[lead.stageId].push(lead);
        }
      });

      return grouped;
    },
    enabled: !!currentOrganization?.organizationId && !!pipelineId && stages.length > 0,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch a single lead by ID.
 *
 * Uses initialData pattern for instant display when navigating from list views.
 * If the lead exists in any list cache, it's shown immediately while fresh data loads.
 *
 * @example
 * ```tsx
 * const { data: lead, isLoading, error } = useLead(leadId);
 *
 * if (isLoading) return <Skeleton />;
 * if (!lead) return <NotFound />;
 *
 * return <LeadDetail lead={lead} />;
 * ```
 *
 * @param leadId - Lead document ID (optional - query disabled if undefined)
 *
 * @returns React Query result with Lead | null data
 */
/** Read one lead. Shared by `useLead` and hover prefetches so both cache the same thing. */
export async function fetchLead(leadId: string): Promise<Lead | null> {
  if (!leadId) return null;

  const docRef = doc(db, COLLECTIONS.LEADS, leadId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) return null;

  return {
    ...docSnap.data(),
    leadId: docSnap.id,
  } as Lead;
}

export function useLead(leadId?: string) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: leadKeys.detail(leadId || ''),
    queryFn: () => fetchLead(leadId || ''),
    enabled: !!leadId,
    staleTime: 1000 * 60 * 5,
    // Use cached data from list views as initial data for instant display
    initialData: () => {
      if (!leadId) return undefined;

      // Try to find the lead in any list cache
      const listQueries = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });

      for (const [, data] of listQueries) {
        if (Array.isArray(data)) {
          const found = data.find((l) => l.leadId === leadId);
          if (found) return found;
        }
      }

      // Also check byStage caches (kanban view)
      const stageQueries = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });

      for (const [, data] of stageQueries) {
        if (data) {
          for (const stageLeads of Object.values(data)) {
            const found = stageLeads.find((l) => l.leadId === leadId);
            if (found) return found;
          }
        }
      }

      return undefined;
    },
    // Track when the initial data was last updated
    initialDataUpdatedAt: () => {
      const state = queryClient.getQueryState(leadKeys.lists());
      return state?.dataUpdatedAt;
    },
  });
}

// ============================================
// LEAD MUTATIONS
// ============================================

/**
 * Create a new lead in the current organization.
 *
 * Automatically sets organizationId, createdAt, updatedAt, createdBy, and
 * initializes stage history.
 *
 * @example
 * ```tsx
 * const createLead = useCreateLead();
 *
 * const handleCreate = async () => {
 *   try {
 *     const leadId = await createLead.mutateAsync({
 *       companyName: 'Acme Corp',
 *       pipelineId: pipeline.pipelineId,
 *       stageId: initialStage.stageId,
 *       stageName: initialStage.name,
 *       stageOrder: initialStage.order,
 *       status: 'open',
 *       serviceTags: ['install'],
 *     });
 *     navigate(`/crm/leads/${leadId}`);
 *   } catch (error) {
 *     toast.error('Failed to create lead');
 *   }
 * };
 * ```
 *
 * @returns Mutation that accepts lead data and returns the new lead ID
 */
export function useCreateLead() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async (
      leadData: Omit<Lead, 'leadId' | 'organizationId' | 'createdAt' | 'updatedAt' | 'createdBy' | 'stageHistory' | 'stageEnteredAt'>
    ): Promise<string> => {
      if (!currentOrganization?.organizationId) {
        throw new Error('No organization selected');
      }
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const callable = httpsCallable<unknown, { leadId: string }>(functions, 'crm_dispatch');
      const result = await callable({
        action: 'createLead',
        ...leadData,
        organizationId: currentOrganization.organizationId,
      });
      return result.data.leadId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

/**
 * The wire form of a lead update. A key whose value is `undefined` means
 * "clear this field" and is sent as an explicit `null`, which the server's
 * updateLead writes as a field delete (#3468). Keys the caller leaves out are
 * not sent at all and stay untouched.
 */
export function toLeadUpdatePayload(updates: Partial<Lead>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(updates).map(([key, value]) => [key, value === undefined ? null : value])
  );
}

/**
 * Update an existing lead with partial data.
 *
 * To clear a field, include its key with the value `undefined`; to leave a
 * field alone, omit the key. The server sets updatedAt.
 *
 * @example
 * ```tsx
 * const updateLead = useUpdateLead();
 *
 * // Inline field update
 * await updateLead.mutateAsync({
 *   leadId: lead.leadId,
 *   updates: { phone: '(555) 123-4567' },
 * });
 *
 * // Update multiple fields
 * await updateLead.mutateAsync({
 *   leadId: lead.leadId,
 *   updates: {
 *     companyName: 'New Name',
 *     priority: 'high',
 *     estimatedValue: 50000,
 *   },
 * });
 * ```
 *
 * @returns Mutation that accepts { leadId, updates } and returns void
 */
export function useUpdateLead() {
  const queryClient = useQueryClient();

  return useMutation(withNeedsSignal('save this lead', {
    mutationFn: async ({
      leadId,
      updates,
    }: {
      leadId: string;
      updates: Partial<Lead>;
    }): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'updateLead', leadId, updates: toLeadUpdatePayload(updates) });
    },
    // Optimistic update for instant UI feedback
    onMutate: async ({ leadId, updates }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      const previousByStage = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });
      const previousDetail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));

      const now = Timestamp.now();

      // Optimistically update list caches
      queryClient.setQueriesData<Lead[]>({ queryKey: leadKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((lead) =>
          lead.leadId === leadId
            ? { ...lead, ...updates, updatedAt: now }
            : lead
        );
      });

      // Optimistically update byStage caches (kanban)
      queryClient.setQueriesData<Record<string, Lead[]>>(
        { queryKey: [...leadKeys.all, 'list', 'byStage'] },
        (old) => {
          if (!old) return old;
          const newData: Record<string, Lead[]> = {};
          for (const [stageId, leads] of Object.entries(old)) {
            newData[stageId] = leads.map((lead) =>
              lead.leadId === leadId
                ? { ...lead, ...updates, updatedAt: now }
                : lead
            );
          }
          return newData;
        }
      );

      // Optimistically update detail cache
      if (previousDetail) {
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), {
          ...previousDetail,
          ...updates,
          updatedAt: now,
        });
      }

      // Capture the previous ownerId for dailyActions cache invalidation on reassign
      let previousOwnerId: string | undefined;
      if ('ownerId' in updates) {
        previousOwnerId = previousDetail?.ownerId;
        // If not in detail cache, search list caches
        if (!previousOwnerId && previousLists) {
          for (const [, leads] of previousLists) {
            if (!leads || !Array.isArray(leads)) continue;
            const found = leads.find((l) => l.leadId === leadId);
            if (found?.ownerId) {
              previousOwnerId = found.ownerId;
              break;
            }
          }
        }
      }

      return { previousLists, previousByStage, previousDetail, previousOwnerId };
    },
    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousByStage) {
        for (const [queryKey, data] of context.previousByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(leadKeys.detail(variables.leadId), context.previousDetail);
      }
    },
    onSuccess: (_, variables) => {
      // When a pipelineId change was included, poll once after a short delay to
      // surface any auto-unenrollments made by the onLeadPipelineChange trigger.
      if (!('pipelineId' in variables.updates)) return;
      setTimeout(async () => {
        try {
          const leadSnap = await getDoc(doc(db, COLLECTIONS.LEADS, variables.leadId));
          const data = leadSnap.data();
          const now = Date.now();
          const recentUnenrolls = ((data?.enrolledSequences ?? []) as Array<Record<string, unknown>>).filter(
            (e) =>
              e.status === 'unenrolled_pipeline_change' &&
              typeof e.unenrolledAt === 'string' &&
              now - new Date(e.unenrolledAt).getTime() < 10_000
          );
          if (recentUnenrolls.length > 0) {
            const count = recentUnenrolls.length;
            toast(
              `Lead moved to new pipeline — auto-unenrolled from ${count} mismatched sequence${count === 1 ? '' : 's'}`,
              { icon: '⚠️' }
            );
          }
        } catch {
          // Non-critical — silently ignore check failures
        }
      }, 2000);
    },
    onSettled: (_, __, variables, context) => {
      // Always revalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.leadId) });
      // Invalidate daily actions only when action-relevant fields change
      const actionRelevantFields = ['status', 'ownerId', 'pipelineId', 'stageId'];
      if (actionRelevantFields.some((f) => f in variables.updates)) {
        queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
      }
      // Server-side callable handles dailyActions cache cleanup on ownerId change
    },
  }));
}

/**
 * Set or clear custom field values on a lead. `null` clears a value. The server
 * validates each value against the org's definitions and rejects the whole call
 * on any invalid value, so the optimistic update is rolled back on error.
 */
export function useSetLeadCustomFieldValues() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadId,
      values,
    }: {
      leadId: string;
      values: Record<string, string | number | string[] | null>;
    }): Promise<{ updatedFieldIds: string[]; clearedFieldIds: string[] }> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      const result = await callable({ action: 'setLeadCustomFieldValues', leadId, values });
      return result.data as { updatedFieldIds: string[]; clearedFieldIds: string[] };
    },
    onMutate: async ({ leadId, values }) => {
      await queryClient.cancelQueries({ queryKey: leadKeys.detail(leadId) });
      const previous = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));
      if (previous) {
        const nextCustomFields = Object.entries(values).reduce<Record<string, CustomFieldValue>>(
          (acc, [fieldId, value]) => {
            if (value === null) {
              return Object.fromEntries(Object.entries(acc).filter(([k]) => k !== fieldId));
            }
            return { ...acc, [fieldId]: value };
          },
          { ...(previous.customFields ?? {}) }
        );
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), { ...previous, customFields: nextCustomFields });
      }
      return { previous };
    },
    onError: (_error, { leadId }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(leadKeys.detail(leadId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

/** What crm_dispatch moveLeadStage returns. */
export interface MoveLeadStageResult {
  autoClosedToStatus?: string;
  outcome?: LeadOutcome;
  reopenedToStatus?: string;
}

/**
 * The lead fields a stage move changed beyond the stage itself, from the server's answer.
 * Returns null when the move neither closed nor reopened the lead.
 */
export function statusPatchForMove(result: MoveLeadStageResult | undefined): Partial<Lead> | null {
  if (result?.autoClosedToStatus === 'closed') {
    return { status: 'closed', outcome: result.outcome };
  }
  if (result?.reopenedToStatus === 'open') {
    return { status: 'open', outcome: undefined, outcomeReason: undefined, outcomeAt: undefined };
  }
  return null;
}

/**
 * Apply `patch` to one lead in the list caches (flat arrays and byStage maps) and the detail
 * cache, as new objects. Other cache shapes under `lists()` are left as they are; the
 * per-column board caches ('paginated-stage') refetch through the onSettled invalidation.
 */
function patchLeadInCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  leadId: string,
  patch: Partial<Lead>
): void {
  const isTheLead = (item: unknown): boolean =>
    !!item && typeof item === 'object' && (item as Lead).leadId === leadId;
  const patchArray = (items: unknown[]): unknown[] =>
    items.map((item) => (isTheLead(item) ? { ...(item as Lead), ...patch } : item));
  const isLeadArray = (value: unknown): value is unknown[] =>
    Array.isArray(value) && value.every((item) => !!item && typeof item === 'object');

  queryClient.setQueriesData<unknown>({ queryKey: leadKeys.lists() }, (old: unknown) => {
    if (isLeadArray(old)) return patchArray(old);
    if (!old || typeof old !== 'object' || Array.isArray(old)) return old;
    const entries = Object.entries(old as Record<string, unknown>);
    // A byStage map is { stageId: Lead[] }; anything else is not ours to rewrite.
    if (!entries.every(([, v]) => isLeadArray(v))) return old;
    return Object.fromEntries(entries.map(([stageId, leads]) => [stageId, patchArray(leads as unknown[])]));
  });
  const detail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));
  if (detail) queryClient.setQueryData<Lead>(leadKeys.detail(leadId), { ...detail, ...patch });
}

/**
 * Move a lead to a different pipeline stage.
 *
 * Updates stage history with entry/exit timestamps and logs a stage_change activity.
 * Use this for drag-and-drop stage changes in the Kanban view.
 *
 * @example
 * ```tsx
 * const moveStage = useMoveLeadStage();
 *
 * const handleDragEnd = async (event: DragEndEvent) => {
 *   const { active, over } = event;
 *   if (!over) return;
 *
 *   const newStage = stages.find(s => s.stageId === over.id);
 *   if (newStage) {
 *     await moveStage.mutateAsync({
 *       leadId: active.id,
 *       newStageId: newStage.stageId,
 *       newStageName: newStage.name,
 *       newStageOrder: newStage.order,
 *     });
 *   }
 * };
 * ```
 *
 * @returns Mutation that accepts stage change params and resolves to the server's
 *   close / reopen answer (also applied to the caches on success)
 */
export function useMoveLeadStage() {
  const queryClient = useQueryClient();
  const { firebaseUser } = useAuth();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      leadId,
      newStageId,
      newStageName,
      newStageOrder,
      nextStepDue,
      nextStep,
    }: {
      leadId: string;
      newStageId: string;
      newStageName: string;
      newStageOrder: number;
      nextStepDue?: Timestamp;
      nextStep?: string;
    }): Promise<MoveLeadStageResult> => {
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }
      if (!currentOrganization?.organizationId) {
        throw new Error('Organization not loaded');
      }

      // The server reads the target stage from the lead's pipeline and decides whether the move
      // closes the lead and with what outcome (Nick, 2026-10-05). stageName/stageOrder are sent
      // for older servers only; terminal flags are never sent.
      const callable = httpsCallable<Record<string, unknown>, MoveLeadStageResult>(functions, 'crm_dispatch');
      const result = await callable({
        action: 'moveLeadStage',
        leadId,
        stageId: newStageId,
        stageName: newStageName,
        stageOrder: newStageOrder,
        nextStep,
        nextStepDue: nextStepDue?.toMillis(),
        organizationId: currentOrganization.organizationId,
      });
      return result.data ?? {};
    },
    // Optimistic update for instant Kanban feedback
    onMutate: async ({ leadId, newStageId, newStageName, newStageOrder }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      const previousByStage = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });
      const previousDetail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));

      const now = Timestamp.now();

      // Optimistically update list caches (only arrays, skip byStage objects)
      queryClient.setQueriesData<Lead[]>({ queryKey: leadKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((lead) =>
          lead.leadId === leadId
            ? {
                ...lead,
                stageId: newStageId,
                stageName: newStageName,
                stageOrder: newStageOrder,
                stageEnteredAt: now,
                updatedAt: now,
              }
            : lead
        );
      });

      // Optimistically update byStage caches (kanban)
      queryClient.setQueriesData<Record<string, Lead[]>>(
        { queryKey: [...leadKeys.all, 'list', 'byStage'] },
        (old) => {
          if (!old) return old;
          const newData: Record<string, Lead[]> = {};

          for (const [stageId, leads] of Object.entries(old)) {
            if (stageId === newStageId) {
              // Add to new stage (lead will be removed from old stage below)
              const lead = Object.values(old)
                .flat()
                .find((l) => l.leadId === leadId);
              if (lead) {
                const updatedLead = {
                  ...lead,
                  stageId: newStageId,
                  stageName: newStageName,
                  stageOrder: newStageOrder,
                  stageEnteredAt: now,
                  updatedAt: now,
                };
                newData[stageId] = [...leads.filter((l) => l.leadId !== leadId), updatedLead];
              } else {
                newData[stageId] = leads;
              }
            } else {
              // Remove from old stages
              newData[stageId] = leads.filter((l) => l.leadId !== leadId);
            }
          }

          return newData;
        }
      );

      // Optimistically update detail cache
      if (previousDetail) {
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), {
          ...previousDetail,
          stageId: newStageId,
          stageName: newStageName,
          stageOrder: newStageOrder,
          stageEnteredAt: now,
          updatedAt: now,
        });
      }

      return { previousLists, previousByStage, previousDetail };
    },
    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousByStage) {
        for (const [queryKey, data] of context.previousByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(leadKeys.detail(variables.leadId), context.previousDetail);
      }
    },
    // The move's close / reopen is decided by the server; reflect it so the board never shows a
    // closed lead as "open" while the refetch below is in flight.
    onSuccess: (result, { leadId }) => {
      const patch = statusPatchForMove(result);
      if (patch) patchLeadInCaches(queryClient, leadId, patch);
    },
    onSettled: (_, __, variables) => {
      // Always revalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.leadId) });
      queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
    },
  });
}

/**
 * Close a lead with an outcome (won/lost/no_decision).
 *
 * Sets status to 'closed', records outcome and reason, closes stage history,
 * and logs a status_change activity.
 *
 * @example
 * ```tsx
 * const closeLead = useCloseLead();
 *
 * const handleWon = async () => {
 *   await closeLead.mutateAsync({
 *     leadId: lead.leadId,
 *     outcome: 'won',
 *     outcomeReason: 'Competitive pricing',
 *     notes: 'Customer signed 3-year contract',
 *   });
 *   toast.success('Lead marked as won!');
 * };
 *
 * const handleLost = async (reason: string) => {
 *   await closeLead.mutateAsync({
 *     leadId: lead.leadId,
 *     outcome: 'lost',
 *     outcomeReason: reason,
 *   });
 * };
 * ```
 *
 * @returns Mutation that accepts close params and returns void
 */
export function useCloseLead() {
  const queryClient = useQueryClient();
  const { firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async ({
      leadId,
      outcome,
      outcomeReason,
      notes,
    }: {
      leadId: string;
      outcome: LeadOutcome;
      outcomeReason?: string;
      notes?: string;
    }): Promise<void> => {
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'closeLead', leadId, outcome, outcomeReason, notes });
    },
    // Optimistic update for instant UI feedback
    onMutate: async ({ leadId, outcome, outcomeReason }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      const previousByStage = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });
      const previousDetail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));

      const now = Timestamp.now();
      const closeUpdates = {
        status: 'closed' as const,
        outcome,
        outcomeReason,
        outcomeAt: now,
        updatedAt: now,
      };

      // Optimistically update list caches
      queryClient.setQueriesData<Lead[]>({ queryKey: leadKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((lead) =>
          lead.leadId === leadId
            ? { ...lead, ...closeUpdates }
            : lead
        );
      });

      // Optimistically update byStage caches (kanban)
      queryClient.setQueriesData<Record<string, Lead[]>>(
        { queryKey: [...leadKeys.all, 'list', 'byStage'] },
        (old) => {
          if (!old) return old;
          const newData: Record<string, Lead[]> = {};
          for (const [stageId, leads] of Object.entries(old)) {
            newData[stageId] = leads.map((lead) =>
              lead.leadId === leadId
                ? { ...lead, ...closeUpdates }
                : lead
            );
          }
          return newData;
        }
      );

      // Optimistically update detail cache
      if (previousDetail) {
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), {
          ...previousDetail,
          ...closeUpdates,
        });
      }

      return { previousLists, previousByStage, previousDetail };
    },
    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousByStage) {
        for (const [queryKey, data] of context.previousByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(leadKeys.detail(variables.leadId), context.previousDetail);
      }
    },
    onSettled: (_, __, variables) => {
      // Always revalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.leadId) });
      queryClient.invalidateQueries({ queryKey: leadKeys.activities(variables.leadId) });
      queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
    },
  });
}

/**
 * Reopen a previously closed lead.
 *
 * Resets status to 'open', clears outcome fields, adds new stage history entry,
 * and logs a status_change activity.
 *
 * @example
 * ```tsx
 * const reopenLead = useReopenLead();
 *
 * const handleReopen = async () => {
 *   await reopenLead.mutateAsync(lead.leadId);
 *   toast.success('Lead reopened');
 * };
 * ```
 *
 * @returns Mutation that accepts leadId and returns void
 */
export function useReopenLead() {
  const queryClient = useQueryClient();
  const { firebaseUser } = useAuth();

  return useMutation({
    mutationFn: async (leadId: string): Promise<void> => {
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'reopenLead', leadId });
    },
    // Optimistic update for instant UI feedback
    onMutate: async (leadId) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      const previousByStage = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });
      const previousDetail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));

      const now = Timestamp.now();
      const reopenUpdates = {
        status: 'open' as const,
        outcome: undefined,
        outcomeReason: undefined,
        outcomeAt: undefined,
        updatedAt: now,
      };

      // Optimistically update list caches
      queryClient.setQueriesData<Lead[]>({ queryKey: leadKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((lead) =>
          lead.leadId === leadId
            ? { ...lead, ...reopenUpdates }
            : lead
        );
      });

      // Optimistically update byStage caches (kanban)
      queryClient.setQueriesData<Record<string, Lead[]>>(
        { queryKey: [...leadKeys.all, 'list', 'byStage'] },
        (old) => {
          if (!old) return old;
          const newData: Record<string, Lead[]> = {};
          for (const [stageId, leads] of Object.entries(old)) {
            newData[stageId] = leads.map((lead) =>
              lead.leadId === leadId
                ? { ...lead, ...reopenUpdates }
                : lead
            );
          }
          return newData;
        }
      );

      // Optimistically update detail cache
      if (previousDetail) {
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), {
          ...previousDetail,
          ...reopenUpdates,
        });
      }

      return { previousLists, previousByStage, previousDetail };
    },
    onError: (_, leadId, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousByStage) {
        for (const [queryKey, data] of context.previousByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(leadKeys.detail(leadId), context.previousDetail);
      }
      toast.error('Failed to reopen lead');
    },
    onSettled: (_, __, leadId) => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(leadId) });
      queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
    },
  });
}

/**
 * Delete a lead and its activities subcollection.
 *
 * Permanently removes the lead document and all associated activity records.
 * This operation cannot be undone.
 *
 * @example
 * ```tsx
 * const deleteLead = useDeleteLead();
 *
 * const handleDelete = async () => {
 *   if (confirm('Are you sure? This cannot be undone.')) {
 *     await deleteLead.mutateAsync(lead.leadId);
 *     navigate('/crm/leads');
 *   }
 * };
 * ```
 *
 * @returns Mutation that accepts leadId and returns void
 */
export function useDeleteLead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId: string): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'deleteLead', leadId });
    },
    // Optimistic update for instant UI feedback
    onMutate: async (leadId) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      const previousByStage = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });
      const previousDetail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));

      // Optimistically remove from list caches
      queryClient.setQueriesData<Lead[]>({ queryKey: leadKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.filter((lead) => lead.leadId !== leadId);
      });

      // Optimistically remove from byStage caches (kanban)
      queryClient.setQueriesData<Record<string, Lead[]>>(
        { queryKey: [...leadKeys.all, 'list', 'byStage'] },
        (old) => {
          if (!old) return old;
          const newData: Record<string, Lead[]> = {};
          for (const [stageId, leads] of Object.entries(old)) {
            newData[stageId] = leads.filter((lead) => lead.leadId !== leadId);
          }
          return newData;
        }
      );

      // Clear detail cache (use setQueryData so rollback works)
      queryClient.setQueryData(leadKeys.detail(leadId), undefined);

      return { previousLists, previousByStage, previousDetail };
    },
    onError: (_, leadId, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousByStage) {
        for (const [queryKey, data] of context.previousByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(leadKeys.detail(leadId), context.previousDetail);
      }
      toast.error('Failed to delete lead');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
      queryClient.invalidateQueries({ queryKey: dailyActionsKeys.all });
    },
  });
}

/**
 * Bulk update multiple leads at once.
 *
 * Uses Firestore batch writes for atomic updates. Useful for reassigning
 * owners, changing priorities, or adding tags to multiple leads.
 *
 * @example
 * ```tsx
 * const bulkUpdate = useBulkUpdateLeads();
 *
 * const handleReassign = async (leadIds: string[], newOwnerId: string) => {
 *   await bulkUpdate.mutateAsync({
 *     leadIds,
 *     updates: {
 *       ownerId: newOwnerId,
 *       ownerName: 'John Smith',
 *     },
 *   });
 *   toast.success(`${leadIds.length} leads reassigned`);
 * };
 * ```
 *
 * @returns Mutation that accepts { leadIds, updates } and returns void
 */
export function useBulkUpdateLeads() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      leadIds,
      updates,
    }: {
      leadIds: string[];
      updates: Partial<Pick<Lead, 'ownerId' | 'ownerName' | 'branch' | 'priority' | 'tags'>>;
    }): Promise<void> => {
      const callable = httpsCallable(functions, 'crm_dispatch');
      await callable({ action: 'bulkUpdateLeads', leadIds, updates });
    },
    // Optimistic update for instant UI feedback
    onMutate: async ({ leadIds, updates }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.all });

      // Snapshot previous state for rollback
      const previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
      const previousByStage = queryClient.getQueriesData<Record<string, Lead[]>>({
        queryKey: [...leadKeys.all, 'list', 'byStage'],
      });
      const previousDetails: Array<[string, Lead]> = [];
      for (const leadId of leadIds) {
        const detail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));
        if (detail) previousDetails.push([leadId, detail]);
      }

      const now = Timestamp.now();
      const optimisticUpdates = { ...updates, updatedAt: now };

      // Optimistically patch list caches
      queryClient.setQueriesData<Lead[]>({ queryKey: leadKeys.lists() }, (old) => {
        if (!old || !Array.isArray(old)) return old;
        return old.map((lead) =>
          leadIds.includes(lead.leadId) ? { ...lead, ...optimisticUpdates } : lead
        );
      });

      // Optimistically patch byStage caches (kanban)
      queryClient.setQueriesData<Record<string, Lead[]>>(
        { queryKey: [...leadKeys.all, 'list', 'byStage'] },
        (old) => {
          if (!old) return old;
          const newData: Record<string, Lead[]> = {};
          for (const [stageId, leads] of Object.entries(old)) {
            newData[stageId] = leads.map((lead) =>
              leadIds.includes(lead.leadId) ? { ...lead, ...optimisticUpdates } : lead
            );
          }
          return newData;
        }
      );

      // Optimistically patch cached detail entries for affected leads
      for (const [leadId, detail] of previousDetails) {
        queryClient.setQueryData<Lead>(leadKeys.detail(leadId), {
          ...detail,
          ...optimisticUpdates,
        });
      }

      return { previousLists, previousByStage, previousDetails };
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousByStage) {
        for (const [queryKey, data] of context.previousByStage) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      if (context?.previousDetails) {
        for (const [leadId, data] of context.previousDetails) {
          queryClient.setQueryData(leadKeys.detail(leadId), data);
        }
      }
      toast.error('Failed to update leads');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: leadKeys.all });
    },
  });
}

// ============================================
// LEAD ACTIVITIES
// ============================================

/**
 * Fetch activities for a lead, ordered by creation date (newest first).
 *
 * Returns up to 100 activities including calls, emails, meetings, notes,
 * stage changes, and status changes.
 *
 * @example
 * ```tsx
 * const { data: activities, isLoading } = useLeadActivities(leadId);
 *
 * return (
 *   <ActivityTimeline
 *     activities={activities || []}
 *     isLoading={isLoading}
 *   />
 * );
 * ```
 *
 * @param leadId - Lead document ID (optional - query disabled if undefined)
 *
 * @returns React Query result with LeadActivity[] data
 */
export function useLeadActivities(leadId?: string) {
  return useQuery({
    queryKey: leadKeys.activities(leadId || ''),
    queryFn: async (): Promise<LeadActivity[]> => {
      if (!leadId) return [];

      const q = query(
        collection(db, 'leads', leadId, 'activities'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({
        ...doc.data(),
        activityId: doc.id,
      })) as LeadActivity[];
    },
    enabled: !!leadId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Log an activity for a lead (call, email, meeting, note, etc.).
 *
 * Creates activity record in the lead's activities subcollection and
 * updates lastContactedAt for contact-type activities.
 *
 * @example
 * ```tsx
 * const logActivity = useLogLeadActivity();
 *
 * const handleLogCall = async () => {
 *   await logActivity.mutateAsync({
 *     leadId: lead.leadId,
 *     activity: {
 *       type: 'call',
 *       subject: 'Follow-up call',
 *       description: 'Discussed pricing options',
 *       outcome: 'Scheduled demo for next week',
 *       duration: 15,
 *       isCompleted: true,
 *       completedAt: Timestamp.now(),
 *     },
 *   });
 *   toast.success('Activity logged');
 * };
 * ```
 *
 * @returns Mutation that accepts { leadId, activity } and returns activity ID
 */
export function useLogLeadActivity() {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const { firebaseUser, user } = useAuth();

  return useMutation(withNeedsSignal('add this to the lead timeline', {
    mutationFn: async ({
      leadId,
      activity,
    }: {
      leadId: string;
      activity: Omit<LeadActivity, 'activityId' | 'leadId' | 'organizationId' | 'userId' | 'userName' | 'createdAt' | 'updatedAt'>;
    }): Promise<string> => {
      if (!currentOrganization?.organizationId) {
        throw new Error('No organization selected');
      }
      if (!firebaseUser?.uid) {
        throw new Error('Not authenticated');
      }

      const callable = httpsCallable<unknown, { activityId: string }>(functions, 'crm_dispatch');
      const result = await callable({
        action: 'logLeadActivity',
        leadId,
        activity,
        organizationId: currentOrganization.organizationId,
      });
      return result.data.activityId;
    },
    // Optimistic update for instant activity timeline feedback
    onMutate: async ({ leadId, activity }) => {
      // Cancel any in-flight queries
      await queryClient.cancelQueries({ queryKey: leadKeys.activities(leadId) });

      // Snapshot previous activities for rollback
      const previousActivities = queryClient.getQueryData<LeadActivity[]>(
        leadKeys.activities(leadId)
      );

      const now = Timestamp.now();
      const optimisticActivity: LeadActivity = {
        activityId: `temp-${Date.now()}`, // Temporary ID until server responds
        leadId,
        organizationId: currentOrganization?.organizationId || '',
        userId: firebaseUser?.uid || '',
        userName: user?.displayName || 'Unknown',
        createdAt: now,
        updatedAt: now,
        ...activity,
      };

      // Optimistically prepend new activity to the list (newest first)
      queryClient.setQueryData<LeadActivity[]>(
        leadKeys.activities(leadId),
        (old) => {
          if (!old) return [optimisticActivity];
          return [optimisticActivity, ...old];
        }
      );

      // Mirror, on the client, exactly what leadActivityService writes on the
      // lead document for this activity type — and nothing else.
      const contactTypes = ['call', 'email', 'meeting', 'site_visit'];
      const isContact = contactTypes.includes(activity.type);
      const isNote = activity.type === 'note';

      const patchLead = (prev: Lead): Lead => {
        const next: Lead = { ...prev, updatedAt: now };
        if (isContact) next.lastContactedAt = now;
        if (isNote) {
          next.lastNoteAt = now;
          // ABSENT IS NOT ZERO. A lead the backfill has not reached has no
          // count at all; optimistically writing 1 would claim "1 note" for a
          // lead that may already have ten. Leave it absent and let the
          // server's increment plus the onSettled refetch supply the truth.
          if (typeof prev.noteCount === 'number') next.noteCount = prev.noteCount + 1;
        }
        return next;
      };

      let previousDetail: Lead | undefined;
      // The kanban board reads leads out of the LIST caches, not the detail
      // cache, so a patch that only touched the detail would leave the card's
      // has-notes signal stale until the next refetch.
      let previousLists: Array<[readonly unknown[], Lead[] | undefined]> = [];

      if (isContact || isNote) {
        previousDetail = queryClient.getQueryData<Lead>(leadKeys.detail(leadId));
        if (previousDetail) {
          queryClient.setQueryData<Lead>(leadKeys.detail(leadId), patchLead(previousDetail));
        }

        previousLists = queryClient.getQueriesData<Lead[]>({ queryKey: leadKeys.lists() });
        for (const [key, list] of previousLists) {
          if (!Array.isArray(list)) continue;
          if (!list.some((l) => l?.leadId === leadId)) continue;
          queryClient.setQueryData<Lead[]>(
            key,
            list.map((l) => (l?.leadId === leadId ? patchLead(l) : l))
          );
        }
      }

      return {
        previousActivities,
        previousDetail,
        previousLists,
        optimisticActivityId: optimisticActivity.activityId,
      };
    },
    onError: (_, variables, context) => {
      // Rollback on error
      if (context?.previousActivities) {
        queryClient.setQueryData(
          leadKeys.activities(variables.leadId),
          context.previousActivities
        );
      }
      if (context?.previousDetail) {
        queryClient.setQueryData(
          leadKeys.detail(variables.leadId),
          context.previousDetail
        );
      }
      for (const [key, list] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, list);
      }
    },
    onSettled: (_, __, variables) => {
      // Always revalidate to get real activity ID and ensure consistency
      queryClient.invalidateQueries({ queryKey: leadKeys.activities(variables.leadId) });
      queryClient.invalidateQueries({ queryKey: leadKeys.detail(variables.leadId) });
      // Only the activity types that move a field ON the lead document need
      // the board's list caches refetched — a bare task/other note does not,
      // and invalidating the whole board for it would be gratuitous.
      if (['call', 'email', 'meeting', 'site_visit', 'note'].includes(variables.activity.type)) {
        queryClient.invalidateQueries({ queryKey: leadKeys.lists() });
      }
    },
  }));
}

/**
 * Update an existing activity for a lead.
 *
 * @example
 * ```tsx
 * const updateActivity = useUpdateLeadActivity();
 *
 * const handleUpdate = async () => {
 *   await updateActivity.mutateAsync({
 *     leadId: lead.leadId,
 *     activityId: activity.activityId,
 *     updates: {
 *       subject: 'Updated subject',
 *       description: 'Updated description',
 *     },
 *   });
 * };
 * ```
 */
export function useUpdateLeadActivity() {
  return useMutation({
    mutationFn: async (_params: {
      leadId: string;
      activityId: string;
      updates: Partial<Pick<LeadActivity, 'subject' | 'description' | 'outcome' | 'duration' | 'isCompleted' | 'completedAt'>>;
    }): Promise<void> => {
      // TODO: implement crm_updateActivity backend callable (issue #1491)
      throw new Error('Activity editing is not yet available');
    },
  });
}

/**
 * Delete an activity from a lead.
 *
 * @example
 * ```tsx
 * const deleteActivity = useDeleteLeadActivity();
 *
 * const handleDelete = async () => {
 *   await deleteActivity.mutateAsync({
 *     leadId: lead.leadId,
 *     activityId: activity.activityId,
 *   });
 * };
 * ```
 */
export function useDeleteLeadActivity() {
  return useMutation({
    mutationFn: async (_params: {
      leadId: string;
      activityId: string;
    }): Promise<void> => {
      // TODO: implement crm_deleteActivity backend callable (issue #1491)
      throw new Error('Activity deletion is not yet available');
    },
  });
}

// ============================================
// METRICS & ANALYTICS
// ============================================

/** Snapshot TTL: 23 hours (re-computed nightly at 2 AM PT). */
const PIPELINE_SNAPSHOT_TTL_MS = 23 * 60 * 60 * 1000;

/** Query keys for pipeline snapshot docs. */
const pipelineSnapshotKeys = {
  all: ['pipelineSnapshot'] as const,
  byPipeline: (orgId: string, pipelineId: string) =>
    [...pipelineSnapshotKeys.all, orgId, pipelineId] as const,
};

/** Shape of the pre-computed snapshot document in Firestore. */
interface PipelineSnapshotDoc {
  readonly computedAt: Timestamp;
  readonly pipelineId: string;
  readonly pipelineName: string;
  readonly totalLeads: number;
  readonly openLeads: number;
  readonly closedLeads: number;
  readonly totalValue: number;
  readonly openValue: number;
  readonly wonValue: number;
  readonly winRate: number;
  readonly avgCycleTime: number;
  readonly avgDealValue: number;
  readonly byStage: StageMetrics[];
  readonly byOwner: OwnerMetrics[];
  readonly byBranch: BranchMetrics[];
}

/**
 * Convert a snapshot document into PipelineMetrics.
 */
function snapshotToPipelineMetrics(snap: PipelineSnapshotDoc): PipelineMetrics {
  return {
    pipelineId: snap.pipelineId,
    pipelineName: snap.pipelineName,
    totalLeads: snap.totalLeads,
    openLeads: snap.openLeads,
    closedLeads: snap.closedLeads,
    totalValue: snap.totalValue,
    openValue: snap.openValue,
    wonValue: snap.wonValue,
    winRate: snap.winRate,
    avgCycleTime: snap.avgCycleTime,
    avgDealValue: snap.avgDealValue,
    byStage: snap.byStage,
    byOwner: snap.byOwner,
    byBranch: snap.byBranch,
  };
}

/**
 * Compute pipeline metrics live from a full leads query.
 * Used as fallback when the pre-computed snapshot is absent or stale.
 */
function computeLivePipelineMetrics(
  leads: readonly Lead[],
  pipelineId: string,
  stages: readonly PipelineStage[],
): PipelineMetrics {
  const stageMap = new Map(stages.map((s) => [s.stageId, s]));

  // Initialize metrics
  const metrics: PipelineMetrics = {
    pipelineId,
    pipelineName: '',
    totalLeads: leads.length,
    openLeads: 0,
    closedLeads: 0,
    totalValue: 0,
    openValue: 0,
    wonValue: 0,
    winRate: 0,
    avgCycleTime: 0,
    avgDealValue: 0,
    byStage: [],
    byOwner: [],
    byBranch: [],
  };

  // Stage metrics map
  const stageMetricsMap = new Map<string, StageMetrics>();
  stages.forEach((stage) => {
    stageMetricsMap.set(stage.stageId, {
      stageId: stage.stageId,
      stageName: stage.name,
      stageGroup: stage.group,
      count: 0,
      value: 0,
      avgDaysInStage: 0,
      staleCount: 0,
    });
  });

  // Owner metrics map
  const ownerMetricsMap = new Map<string, OwnerMetrics>();

  // Branch metrics map
  const branchMetricsMap = new Map<string, BranchMetrics>();

  let totalCycleTime = 0;
  let closedWithCycleTime = 0;

  leads.forEach((lead) => {
    const value = lead.estimatedValue || 0;
    metrics.totalValue += value;

    if (lead.status === 'open') {
      metrics.openLeads++;
      metrics.openValue += value;
    } else {
      metrics.closedLeads++;
      if (lead.outcome === 'won') {
        metrics.wonValue += value;
      }
    }

    // Stage metrics
    const stageMet = stageMetricsMap.get(lead.stageId);
    if (stageMet) {
      stageMet.count++;
      stageMet.value += value;

      // Check if stale
      const stage = stageMap.get(lead.stageId);
      if (stage?.rotDays && lead.stageEnteredAt) {
        const enteredMs = toMillisSafe(lead.stageEnteredAt);
        if (enteredMs !== null) {
          const daysInStage = Math.floor(
            (Date.now() - enteredMs) / (1000 * 60 * 60 * 24)
          );
          if (daysInStage > stage.rotDays) {
            stageMet.staleCount++;
          }
        }
      }
    }

    // Owner metrics
    if (lead.ownerId) {
      if (!ownerMetricsMap.has(lead.ownerId)) {
        ownerMetricsMap.set(lead.ownerId, {
          ownerId: lead.ownerId,
          ownerName: lead.ownerName,
          count: 0,
          value: 0,
          wonValue: 0,
          winRate: 0,
        });
      }
      const ownerMet = ownerMetricsMap.get(lead.ownerId)!;
      ownerMet.count++;
      ownerMet.value += value;
      if (lead.outcome === 'won') {
        ownerMet.wonValue += value;
      }
    }

    // Branch metrics
    if (lead.branch) {
      if (!branchMetricsMap.has(lead.branch)) {
        branchMetricsMap.set(lead.branch, {
          branch: lead.branch,
          count: 0,
          value: 0,
          wonValue: 0,
        });
      }
      const branchMet = branchMetricsMap.get(lead.branch)!;
      branchMet.count++;
      branchMet.value += value;
      if (lead.outcome === 'won') {
        branchMet.wonValue += value;
      }
    }

    // Cycle time for closed deals
    if (lead.status === 'closed' && lead.outcomeAt && lead.createdAt) {
      const cycleTime = Math.floor(
        (lead.outcomeAt.toMillis() - lead.createdAt.toMillis()) / (1000 * 60 * 60 * 24)
      );
      totalCycleTime += cycleTime;
      closedWithCycleTime++;
    }
  });

  // Calculate rates
  const closedWonLost = leads.filter(
    (l) => l.status === 'closed' && (l.outcome === 'won' || l.outcome === 'lost')
  ).length;
  const wonCount = leads.filter((l) => l.outcome === 'won').length;
  metrics.winRate = closedWonLost > 0 ? (wonCount / closedWonLost) * 100 : 0;
  metrics.avgCycleTime = closedWithCycleTime > 0 ? totalCycleTime / closedWithCycleTime : 0;
  metrics.avgDealValue = metrics.closedLeads > 0 ? metrics.wonValue / wonCount : 0;

  // Calculate owner win rates
  ownerMetricsMap.forEach((owner) => {
    const ownerLeads = leads.filter((l) => l.ownerId === owner.ownerId);
    const ownerClosed = ownerLeads.filter(
      (l) => l.status === 'closed' && (l.outcome === 'won' || l.outcome === 'lost')
    ).length;
    const ownerWon = ownerLeads.filter((l) => l.outcome === 'won').length;
    owner.winRate = ownerClosed > 0 ? (ownerWon / ownerClosed) * 100 : 0;
  });

  metrics.byStage = Array.from(stageMetricsMap.values()).sort(
    (a, b) => stages.findIndex((s) => s.stageId === a.stageId) - stages.findIndex((s) => s.stageId === b.stageId)
  );
  metrics.byOwner = Array.from(ownerMetricsMap.values()).sort((a, b) => b.value - a.value);
  metrics.byBranch = Array.from(branchMetricsMap.values()).sort((a, b) => b.value - a.value);

  return metrics;
}

/**
 * Get comprehensive metrics for a pipeline.
 *
 * Reads from the nightly pre-computed snapshot for fast initial load (<200ms).
 * Falls back to live computation when the snapshot is absent or stale (>23h).
 *
 * @example
 * ```tsx
 * const { data: metrics, isLoading } = usePipelineMetrics(
 *   pipeline.pipelineId,
 *   pipeline.stages
 * );
 *
 * if (metrics) {
 *   console.log(`Win rate: ${metrics.winRate.toFixed(1)}%`);
 *   console.log(`Avg cycle: ${metrics.avgCycleTime} days`);
 *   console.log(`Pipeline value: $${metrics.totalValue}`);
 * }
 * ```
 *
 * @param pipelineId - Pipeline ID to calculate metrics for
 * @param stages - Stage configurations (used for grouping and stale detection)
 *
 * @returns React Query result with PipelineMetrics data
 */
export function usePipelineMetrics(pipelineId: string, stages: PipelineStage[]) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';

  // ── Snapshot query (fast, single doc read) ──────────────────────────────
  const { data: snapshotDoc, isLoading: snapshotLoading } = useQuery<PipelineSnapshotDoc | null>({
    queryKey: pipelineSnapshotKeys.byPipeline(orgId, pipelineId),
    queryFn: async (): Promise<PipelineSnapshotDoc | null> => {
      if (!orgId || !pipelineId) return null;
      const ref = doc(
        db,
        'organizations',
        orgId,
        'crmSettings',
        `pipelineSnapshot_${pipelineId}`,
      );
      const snap = await getDoc(ref);
      return snap.exists() ? (snap.data() as PipelineSnapshotDoc) : null;
    },
    enabled: !!orgId && !!pipelineId,
    staleTime: PIPELINE_SNAPSHOT_TTL_MS,
    gcTime: PIPELINE_SNAPSHOT_TTL_MS + 60_000,
  });

  // Determine if snapshot is fresh enough to use
  const snapshotIsFresh = useMemo(() => {
    if (!snapshotDoc?.computedAt) return false;
    try {
      return Date.now() - snapshotDoc.computedAt.toDate().getTime() < PIPELINE_SNAPSHOT_TTL_MS;
    } catch {
      return false;
    }
  }, [snapshotDoc]);

  // ── Live query (fallback when snapshot is missing/stale) ────────────────
  const { data: liveMetrics, isLoading: liveLoading } = useQuery({
    queryKey: leadKeys.metrics(pipelineId),
    queryFn: async (): Promise<PipelineMetrics | null> => {
      if (!orgId || !pipelineId) {
        return null;
      }

      const q = query(
        collection(db, COLLECTIONS.LEADS),
        where('organizationId', '==', orgId),
        where('pipelineId', '==', pipelineId)
      );

      const leadsSnap = await getDocs(q);
      const leads = leadsSnap.docs
        .map((d) => d.data() as Lead)
        .filter((l) => l.isArchived !== true);

      return computeLivePipelineMetrics(leads, pipelineId, stages);
    },
    // Only run live query if snapshot is not available or stale
    enabled: !!orgId && !!pipelineId && stages.length > 0 && !snapshotLoading && !snapshotIsFresh,
    staleTime: 1000 * 60 * 5,
  });

  // ── Merge results ───────────────────────────────────────────────────────
  const data = useMemo((): PipelineMetrics | null | undefined => {
    if (snapshotIsFresh && snapshotDoc) {
      return snapshotToPipelineMetrics(snapshotDoc);
    }
    return liveMetrics;
  }, [snapshotIsFresh, snapshotDoc, liveMetrics]);

  const isLoading = snapshotLoading || (!snapshotIsFresh && liveLoading);

  return { data, isLoading };
}

/**
 * Get overall lead statistics across all pipelines.
 *
 * Returns aggregate counts (total, open, closed, won, lost) and values
 * for the entire organization.
 *
 * @example
 * ```tsx
 * const { data: stats, isLoading } = useLeadStats();
 *
 * return (
 *   <StatsBar>
 *     <Stat label="Total Leads" value={stats?.total || 0} />
 *     <Stat label="Open" value={stats?.open || 0} />
 *     <Stat label="Won" value={stats?.won || 0} />
 *     <Stat label="Pipeline Value" value={formatCurrency(stats?.openValue || 0)} />
 *   </StatsBar>
 * );
 * ```
 *
 * @returns React Query result with lead statistics object
 */
export function useLeadStats() {
  const { currentOrganization } = useOrganization();

  return useQuery({
    queryKey: leadKeys.stats(),
    queryFn: async () => {
      if (!currentOrganization?.organizationId) {
        return null;
      }

      const q = query(
        collection(db, COLLECTIONS.LEADS),
        where('organizationId', '==', currentOrganization.organizationId)
      );

      const snapshot = await getDocs(q);
      // Filter archived leads client-side (avoids Firestore != operator which
      // forces implicit orderBy and requires composite indexes)
      const leads = snapshot.docs
        .map((doc) => doc.data() as Lead)
        .filter((l) => l.isArchived !== true);

      return {
        total: leads.length,
        open: leads.filter((l) => l.status === 'open').length,
        closed: leads.filter((l) => l.status === 'closed').length,
        won: leads.filter((l) => l.outcome === 'won').length,
        lost: leads.filter((l) => l.outcome === 'lost').length,
        noDecision: leads.filter((l) => l.outcome === 'no_decision').length,
        totalValue: leads.reduce((sum, l) => sum + (l.estimatedValue || 0), 0),
        openValue: leads
          .filter((l) => l.status === 'open')
          .reduce((sum, l) => sum + (l.estimatedValue || 0), 0),
        wonValue: leads
          .filter((l) => l.outcome === 'won')
          .reduce((sum, l) => sum + (l.estimatedValue || 0), 0),
        byPipeline: {} as Record<string, number>,
      };
    },
    enabled: !!currentOrganization?.organizationId,
    staleTime: 1000 * 60 * 5,
  });
}
